// ════════════════════════════════════════════════════════════════════════
//  Tipp-Engine
//
//  Reiner Zustandsautomat: Zustand + Eingabe → neuer Zustand. Kein React, keine
//  Timer, kein DOM. Damit ist die komplette Lernlogik ohne Browser testbar, und
//  die UI kann sich auf Darstellung beschränken.
//
//  Verhalten wie im Piloten-Prototyp: ein falscher Anschlag zählt als Fehler,
//  bewegt die Position aber NICHT. Das Kind tippt weiter, bis das richtige
//  Zeichen kommt – kein Zurückgehen, kein Backspace.
// ════════════════════════════════════════════════════════════════════════

import { fingerIndex } from "../content/schema.js";
import type { Bundle, Finger, Lesson, Stage } from "../content/schema.js";

export type StageState = {
  readonly stageKey: string;
  readonly text: string;
  /** Index des nächsten erwarteten Zeichens. */
  readonly pos: number;
  readonly correct: number;
  readonly errors: number;
  readonly complete: boolean;
};

/** Was ein Anschlag ausgelöst hat – die UI hängt daran Ton, Wackeln, Aufleuchten. */
export type Stroke = "hit" | "miss" | "ignored" | "complete";

/**
 * Auf welchem Weg eine Lektion Großbuchstaben verlangt, und wie ein Anschlag
 * dagegen verstoßen hat. Der Verstoß ist ein Fehler wie jeder andere – aber die
 * App kann damit sagen, WARUM er einer war, statt nur zu wackeln.
 */
export type UppercaseWith = NonNullable<Lesson["uppercaseWith"]>;

export type StageResult = {
  readonly stageKey: string;
  readonly accuracy: number;
  readonly stars: 1 | 2 | 3;
};

export function startStage(stage: Stage): StageState {
  return {
    stageKey: stage.key,
    text: stage.text,
    pos: 0,
    correct: 0,
    errors: 0,
    complete: false,
  };
}

/** Das nächste erwartete Zeichen, oder null wenn die Stufe fertig ist. */
export function target(state: StageState): string | null {
  return state.complete ? null : (state.text[state.pos] ?? null);
}

/**
 * Genauigkeit als Anteil richtiger Anschläge.
 *
 * Vor dem ersten Anschlag ist sie 1 und nicht NaN: es ist noch nichts falsch
 * gelaufen. Der Prototyp hat hier 0/0 gerechnet, was in der Anzeige als NaN
 * durchschlug.
 */
export function accuracy(state: StageState): number {
  const total = state.correct + state.errors;
  return total === 0 ? 1 : state.correct / total;
}

/**
 * Wörter pro Minute – Nettowörter zu fünf Zeichen.
 *
 * Fünf Zeichen je Wort ist die internationale Konvention und macht die Zahl
 * unabhängig davon, wie lang die Wörter des Testtexts sind. Gezählt werden die
 * RICHTIGEN Zeichen; Fehler gehen über `accuracy` ein und nicht ein zweites Mal
 * hier – sonst zöge ein Vertipper das Ergebnis doppelt herunter.
 *
 * Ohne Zeit ist es 0 und nicht Infinity: wie bei `accuracy` vor dem ersten
 * Anschlag ist das kein Ergebnis, sondern der Zustand „noch nichts gemessen“.
 * Gerundet wird erst bei der Anzeige – gespeichert werden Zeichen und Sekunden.
 */
export function wordsPerMinute(measurement: {
  readonly chars: number;
  readonly seconds: number;
}): number {
  if (measurement.seconds <= 0 || measurement.chars <= 0) return 0;
  return measurement.chars / 5 / (measurement.seconds / 60);
}

export function starsFor(acc: number, thresholds: Bundle["starThresholds"]): 1 | 2 | 3 {
  if (acc >= thresholds.three) return 3;
  if (acc >= thresholds.two) return 2;
  return 1;
}

/** Ein Zeichen ist groß, wenn es sich klein schreiben lässt. Gilt auch für Ö und Ä. */
function isUppercase(char: string): boolean {
  return char !== char.toLowerCase();
}

/**
 * Passt der Anschlag auf das erwartete Zeichen?
 *
 * Die Regel ist bewusst einseitig:
 *
 * - Erwartet die Übung einen GROSSBUCHSTABEN, zählt nur der Großbuchstabe.
 *   Genau das ist der Inhalt von Kapitel 5 – ein kleines a wäre die Aufgabe
 *   nicht gelöst, sondern umgangen.
 * - Erwartet sie einen kleinen, zählt beides. Vor Kapitel 5 hat noch niemand
 *   die Umschalttaste gelernt; ein Kind, das die Feststelltaste erwischt hat,
 *   darf daran nicht die ganze Stufe verlieren.
 *
 * Vorher wurde immer kleingeschrieben verglichen. Eine Stufe mit einem
 * Großbuchstaben wäre damit unlösbar gewesen: das erwartete "A" hätte auf kein
 * kleingeschriebenes Zeichen gepasst, und die Stufe hätte sich nie beendet.
 */
function matches(key: string, expected: string): boolean {
  return isUppercase(expected) ? key === expected : key.toLowerCase() === expected;
}

/**
 * Verarbeitet einen Tastendruck.
 *
 * `key` ist der Rohwert aus `KeyboardEvent.key`. Alles, was nicht genau ein
 * Zeichen ist ("Shift", "ArrowLeft", "Enter"), wird ignoriert statt als Fehler
 * gezählt – ein Kind, das die Umschalttaste streift, soll keinen Fehler kassieren.
 */
export function typeKey(
  state: StageState,
  key: string,
  /**
   * Was die Lektion über den Weg zum Großbuchstaben verlangt, und was der
   * Anschlag mitgebracht hat.
   *
   * Unterschieden wird ausschließlich über `shiftKey`: gehalten heißt
   * Umschalttaste, nicht gehalten heißt Feststelltaste. Das meldet jeder
   * Browser – `getModifierState("CapsLock")` braucht es dafür nicht, und damit
   * hängt die Prüfung nicht an einer Eigenschaft, die auf dem Zielgerät
   * unbestätigt ist.
   *
   * Alles optional, damit Aufrufe ohne Tastaturereignis tragen.
   */
  options: {
    readonly shiftKey?: boolean;
    readonly uppercaseWith?: UppercaseWith | undefined;
  } = {},
): {
  readonly state: StageState;
  readonly stroke: Stroke;
  /** Gesetzt, wenn der Fehler am Weg lag und nicht am Buchstaben. */
  readonly wrongWay?: UppercaseWith;
} {
  if (state.complete) return { state, stroke: "ignored" };

  if ([...key].length !== 1) return { state, stroke: "ignored" };

  const expected = target(state);
  if (expected === null) return { state, stroke: "ignored" };

  if (!matches(key, expected)) {
    return { state: { ...state, errors: state.errors + 1 }, stroke: "miss" };
  }

  // Der Buchstabe stimmt – aber kam er auf dem Weg, den die Lektion übt?
  // Ein Verstoß ist ein Fehler wie jeder andere: er zählt und rückt nicht vor.
  // Der Grund kommt mit, damit die App ihn nennen kann statt nur zu wackeln.
  const wanted = options.uppercaseWith;
  if (wanted !== undefined && isUppercase(expected)) {
    const used: UppercaseWith = options.shiftKey === true ? "shift" : "capsLock";
    if (used !== wanted) {
      return {
        state: { ...state, errors: state.errors + 1 },
        stroke: "miss",
        wrongWay: wanted,
      };
    }
  }

  const pos = state.pos + 1;
  const correct = state.correct + 1;
  const complete = pos >= state.text.length;
  return {
    state: { ...state, pos, correct, complete },
    stroke: complete ? "complete" : "hit",
  };
}

export function resultOf(
  state: StageState,
  thresholds: Bundle["starThresholds"],
): StageResult {
  const acc = accuracy(state);
  return { stageKey: state.stageKey, accuracy: acc, stars: starsFor(acc, thresholds) };
}

// ────────────────────────────────────────────────────────────────────────
//  Nachschlagen im Bundle
// ────────────────────────────────────────────────────────────────────────

export function findLesson(bundle: Bundle, lessonKey: string): Lesson | null {
  return bundle.lessons.find((l) => l.key === lessonKey) ?? null;
}

export function findStage(
  bundle: Bundle,
  stageKey: string,
): { readonly lesson: Lesson; readonly stage: Stage } | null {
  for (const lesson of bundle.lessons) {
    const stage = lesson.stages.find((s) => s.key === stageKey);
    if (stage) return { lesson, stage };
  }
  return null;
}

/** Die auf die Stufe folgende Stufe derselben Lektion, oder null am Lektionsende. */
export function nextStage(lesson: Lesson, stageKey: string): Stage | null {
  const index = lesson.stages.findIndex((s) => s.key === stageKey);
  if (index < 0) return null;
  return lesson.stages[index + 1] ?? null;
}

export function fingerFor(bundle: Bundle, char: string): Finger | null {
  return fingerIndex(bundle).get(char) ?? null;
}

/** Lektionen in Lehrgangsreihenfolge – `order` ist maßgeblich, nicht der Array-Index. */
export function lessonsInOrder(bundle: Bundle): readonly Lesson[] {
  return [...bundle.lessons].sort((a, b) => a.order - b.order);
}

/**
 * Sterne einer Lektion aus den Stufenergebnissen: Mittel der Genauigkeiten,
 * daraus die Sterne – wie im Prototyp. Fehlende Stufen zählen nicht mit, eine
 * halb geübte Lektion bekommt also keine künstlich schlechte Bewertung.
 */
export function lessonStars(
  results: readonly StageResult[],
  thresholds: Bundle["starThresholds"],
): 1 | 2 | 3 | null {
  if (results.length === 0) return null;
  const avg = results.reduce((sum, r) => sum + r.accuracy, 0) / results.length;
  return starsFor(avg, thresholds);
}
